<template>
    <div class="card">
        <header class="card-header">
            <p class="card-header-title">{{ chartTitle }}</p>

            <div v-if="!isNull(dataType)" class="card-header-icon">
                <span v-tippy :title="$t(dataTypes[dataType].label)">
                    <svg class="svg-icons">
                        <use
                            :xlink:href="
                                require('@/assets/img/icons/' + dataTypes[dataType].svg + '.svg') +
                                '#layer'
                            "
                        ></use>
                    </svg>
                </span>
            </div>
        </header>
        <div class="card-footer card-footer-selector">
            <div
                class="card-footer-item"
                :class="{ selected: chartVisualization === 'pie' }"
                @click="changeChartVisualization('pie')"
            >
                <div class="is-flex g-2">
                    <span class="icon">
                        <i class="fas fa-chart-pie"></i>
                    </span>
                </div>
            </div>
            <div
                class="card-footer-item"
                :class="{ selected: chartVisualization === 'bar' && !isBarChartHorizontal }"
                @click="changeBarChartOrientation('vertical')"
            >
                <div class="is-flex g-2">
                    <span class="icon">
                        <i class="fas fa-chart-column"></i>
                    </span>
                </div>
            </div>
            <div
                class="card-footer-item"
                :class="{ selected: chartVisualization === 'bar' && isBarChartHorizontal }"
                @click="changeBarChartOrientation('horizontal')"
            >
                <div class="is-flex g-2">
                    <span class="icon">
                        <i class="fas fa-chart-bar"></i>
                    </span>
                </div>
            </div>
            <div
                class="card-footer-item"
                :class="{ selected: chartVisualization === 'grid' }"
                @click="changeChartVisualization('grid')"
            >
                <div class="is-flex g-2">
                    <span class="icon">
                        <i class="fas fa-th"></i>
                    </span>
                </div>
            </div>
        </div>
        <div class="chart-separator"></div>
        <section class="card-content p-3">
            <div class="content">
                <apexchart
                    v-if="chartVisualization === 'pie'"
                    type="pie"
                    :options="mapOptionsToPieChart"
                    :series="filteredChartData"
                />
                <apexchart
                    v-if="chartVisualization === 'bar'"
                    type="bar"
                    :options="mapOptionsToBarChart"
                    :series="mapDataToBarChart"
                />
                <b-table
                    v-if="chartVisualization === 'grid'"
                    :data="mapDataToGridChart"
                    class="data-table table-overflow"
                >
                    <b-table-column
                        v-for="(f, i) in fields"
                        :key="i"
                        :field="f.key"
                        :label="f.label ? $t(f.label) : $t(f.key)"
                        :width="f.width"
                        :sortable="f.sortable"
                        :centered="f.centered"
                        v-slot="props"
                    >
                        <div
                            :title="props.row[f.key]"
                            v-if="f.key == 'respostas' && cutThis.includes(dataType)"
                        >
                            {{ cutString(props.row[f.key], 100) }}
                        </div>
                        <div v-else>
                            {{ props.row[f.key] }}
                        </div>
                    </b-table-column>
                </b-table>
            </div>
        </section>
    </div>
</template>

<script>
import { palette } from '@/functions/color/Palette.js';

export default {
    name: 'ChartViewSelector',
    props: {
        chartTitle: {
            type: String,
            default: '',
        },
        chartLabel: {
            type: Array,
            default: [],
        },
        chartData: {
            type: Array,
            default: [],
        },
        dataType: {
            type: String,
        },
    },
    data() {
        // Se não for tipo seleção, inicia com grid
        // let selectionTypes = ['RADIO', 'CHECKBOX', 'COMBOBOX'];
        // let initialVisualization = selectionTypes.includes(this.dataType) ? 'pie' : 'grid';
        return {
            chartVisualization: 'pie',
            isBarChartHorizontal: false,
            colors: [],
            fields: [
                { key: 'qntde', centered: true, sortable: true, width: 80 },
                { key: 'respostas', label: 'respostas-texto', sortable: true },
            ],
            formattedChartLabels: [],
            dataTypes: {
                TEXT: {
                    svg: 'text-short',
                    label: 'area-texto-curto',
                },
                MULTITEXT: {
                    svg: 'text-subject',
                    label: 'area-texto-longo',
                },
                INTEGER: {
                    svg: 'text-short',
                    label: 'campo-numero',
                },
                DECIMAL: {
                    svg: 'currency-usd',
                    label: 'campo-moeda',
                },
                PERCENT: {
                    svg: 'percent',
                    label: 'campo-porcentagem',
                },
                SWITCH: {
                    svg: 'text-short',
                    label: 'disjuntor',
                },
                DATETIME: {
                    svg: 'calendar-clock',
                    label: 'campo-data-hora',
                },
                DATE: {
                    svg: 'calendar',
                    label: 'calendario',
                },
                RADIO: {
                    svg: 'radiobox-marked',
                    label: 'multipla-escolha',
                },
                CHECKBOX: {
                    svg: 'checkbox-marked',
                    label: 'lista-opcoes',
                },
                COMBOBOX: {
                    svg: 'menu-down',
                    label: 'combo-box',
                },
            },
            justGrid: ['TEXT', 'MULTITEXT'],
            cutThis: ['TEXT', 'MULTITEXT', 'SWITCH', 'RADIO', 'CHECKBOX', 'COMBOBOX'],
        };
    },
    created() {
        this.colors = palette.getChartColors();

        this.formattedChartLabels = this.chartLabel.map((label) => this.textFormatter(label));
    },
    computed: {
        // configurações
        mapOptionsToPieChart: function () {
            let temp = this;
            return {
                labels: this.filteredChartLabels,
                colors: this.colors,
                legend: {
                    position: 'bottom',
                    horizontalAlign: this.cutThis.includes(this.dataType) ? 'left' : 'center',
                    labels: {
                        useSeriesColors: true,
                    },
                    formatter: function (val) {
                        return temp.cutThis.includes(temp.dataType) ? temp.cutString(val, 65) : val;
                    },
                },
                tooltip: {
                    y: {
                        title: {
                            formatter: function (val) {
                                return temp.cutThis.includes(temp.dataType)
                                    ? temp.cutString(val)
                                    : val;
                            },
                        },
                    },
                },
            };
        },
        mapOptionsToBarChart: function () {
            const stepSize = this.calculateStepSizeByChartData(this.filteredChartData);
            let temp = this;
            return {
                labels: this.filteredChartLabels,
                colors: this.colors,
                plotOptions: {
                    bar: {
                        horizontal: this.isBarChartHorizontal,
                        borderRadius: 5,
                    },
                },
                xaxis: {
                    stepSize: stepSize,
                    labels: {
                        rotate: -90,
                        trim: true,
                    },
                },
                yaxis: {
                    stepSize: stepSize,
                    labels: {
                        trim: true,
                    },
                },
                tooltip: {
                    x: {
                        formatter: function (val) {
                            return temp.cutThis.includes(temp.dataType) ? temp.cutString(val) : val;
                        },
                    },
                },
            };
        },
        // Dados
        mapDataToBarChart: function () {
            return [
                {
                    name: 'Quantidade',
                    data: this.filteredChartData,
                },
            ];
        },
        mapDataToGridChart: function () {
            return this.chartLabel.map((label, index) => ({
                qntde: this.chartData[index],
                respostas: this.textFormatter(label),
            }));
        },
        // Novo: retorna os dados filtrados para gráficos
        filteredChartData() {
            // Só aplica para tipos não seleção, mais de 10 registros e visualização não grid
            const selectionTypes = ['RADIO', 'CHECKBOX', 'COMBOBOX'];
            if (selectionTypes.includes(this.dataType)) {
                return this.chartData;
            }
            if (this.chartVisualization === 'grid' || this.chartData.length <= 10) {
                return this.chartData;
            }
            // Monta array de objetos para ordenar
            let dataArr = this.chartLabel.map((label, idx) => ({
                label: this.textFormatter(label),
                value: this.chartData[idx] || 0,
            }));
            // Ordena decrescente por quantidade
            dataArr.sort((a, b) => b.value - a.value);
            // Pega os 10 maiores
            let top10 = dataArr.slice(0, 10);
            let outros = dataArr.slice(10);
            let outrosSoma = outros.reduce((acc, cur) => acc + cur.value, 0);
            let result = top10.map((item) => item.value);
            if (outrosSoma > 0) {
                result.push(outrosSoma);
            }
            return result;
        },
        filteredChartLabels() {
            // Só aplica para tipos não seleção, mais de 10 registros e visualização não grid
            const selectionTypes = ['RADIO', 'CHECKBOX', 'COMBOBOX'];
            if (selectionTypes.includes(this.dataType)) {
                return this.formattedChartLabels;
            }
            if (this.chartVisualization === 'grid' || this.chartData.length <= 10) {
                return this.formattedChartLabels;
            }
            // Monta array de objetos para ordenar
            let dataArr = this.chartLabel.map((label, idx) => ({
                label: this.textFormatter(label),
                value: this.chartData[idx] || 0,
            }));
            // Ordena decrescente por quantidade
            dataArr.sort((a, b) => b.value - a.value);
            // Pega os 10 maiores
            let top10 = dataArr.slice(0, 10);
            let outros = dataArr.slice(10);
            let result = top10.map((item) => item.label);
            if (outros.length > 0) {
                result.push(this.$t('outros'));
            }
            return result;
        },
    },
    methods: {
        changeChartVisualization(chartType) {
            this.chartVisualization = chartType;
        },
        changeBarChartOrientation(orientation) {
            this.changeChartVisualization('bar');
            this.isBarChartHorizontal = orientation === 'horizontal';
        },
        textFormatter(value) {
            if (this.isNull(value)) return this.$t('nao-informado');

            switch (this.dataType) {
                case 'DECIMAL':
                    return this.getDecimal(value);
                case 'PERCENT':
                    return this.parseAsPercent(value);
                case 'DATE':
                    return this.$moment(value).format('L');
                case 'DATETIME':
                    return this.$moment(value).format('L LTS');
                default:
                    return value;
            }
        },
        calculateStepSizeByChartData(dataArr) {
            const arr = dataArr || this.chartData;
            const maxValue = Math.max(...arr);
            return Math.ceil(maxValue / 5);
        },
    },
};
</script>

<style lang="scss" scoped>
.table-overflow {
    overflow-y: auto;
    max-height: 385px;
}
.g-2 {
    gap: 8px;
}
.card-footer-item {
    transition: all 0.3s ease-in-out;

    & {
        cursor: pointer;
    }

    &.selected {
        background-color: #f0f0f0;

        .icon {
            color: ($primary-color-click);
        }
    }

    &:hover {
        background-color: #f0f0f0;
    }
}
.card-footer-selector {
    margin-bottom: 0;
}
.chart-separator {
    border-bottom: 1px solid #f5f5f5;
    margin-bottom: 16px;
}
.apexcharts-tooltip .apexcharts-tooltip-y-group {
    display: flex;
    flex-direction: column;
}
</style>
